import React, { useEffect } from 'react';
import ChartCard from './ChartCard';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { useAppDispatch, useAppSelector } from '../../store/hooks/reduxHooks';
import { fetchMerchantByLenderStats } from '../../features/dashboard/dashboardSlice';

const LENDER_COLORS: Record<string, string> = {
  'Cashe': '#3B82F6',
  'Zype': '#22C55E',
};

const MIN_VISIBLE_PERCENTAGE = 3;

const MerchantsByLender: React.FC = () => {
  const dispatch = useAppDispatch();
  const { merchantByLenderStats: stats, loadingMerchantByLender: isLoading, hasFetchedMerchantByLender } = useAppSelector(state => state.dashboard);

  useEffect(() => {
    if (!hasFetchedMerchantByLender && !isLoading) {
      dispatch(fetchMerchantByLenderStats());
    }
  }, [dispatch, hasFetchedMerchantByLender, isLoading]);

  const chartData = stats ? stats.breakdownByLender.map(item => {
    const name = item.lender.charAt(0).toUpperCase() + item.lender.slice(1);
    const realCount = item.count;
    const realPercentage = item.percentage;
    const visualPercentage = realPercentage < MIN_VISIBLE_PERCENTAGE ? Math.max(MIN_VISIBLE_PERCENTAGE, realPercentage) : realPercentage;
    const visualCount = Math.round((visualPercentage * stats.totalMerchants) / 100);

    return {
      name,
      value: visualCount,
      realValue: realCount,
      percentage: realPercentage,
      visualPercentage
    };
  }) : [];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length && stats) {
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-sm rounded">
          <p className="font-gilroy-semibold">{`${payload[0].name}: ${payload[0].payload.realValue.toLocaleString()} merchants`}</p>
          <p className="text-sm text-gray-600">{`${payload[0].payload.percentage.toFixed(2)}% of total`}</p>
        </div>
      );
    }
    return null;
  };

  const renderLegend = (props: any) => {
    const { payload } = props;
    return (
      <div className="flex justify-center flex-wrap gap-4 mt-4">
        {payload.map((entry: any, index: number) => {
          const item = chartData[index];
          return (
            <div key={`legend-${index}`} className="flex items-center">
              <div className="w-3 h-3 mr-2 rounded-sm" style={{ backgroundColor: entry.color }}></div>
              <div>
                <div className="text-sm font-gilroy-semibold">{entry.value}</div>
                <div className="text-xs text-gray-500">{item?.realValue.toLocaleString() || 0} merchants ({item?.percentage.toFixed(2) || 0}%)</div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getMostPopularLender = () => {
    if (!stats || !stats.breakdownByLender.length) return '-';
    const sorted = [...stats.breakdownByLender].sort((a, b) => b.count - a.count);
    return sorted[0].lender;
  };


  return (
    <ChartCard title="Merchants by Lender" subtitle="Distribution of merchants across lending partners" className="h-full">
      {isLoading ? (
        <div className="w-full h-56 flex items-center justify-center">
          <div className="animate-pulse text-gray-400">Loading lender distribution data...</div>
        </div>
      ) : chartData.length > 0 ? (
        <div className="w-full h-56 flex items-center justify-center">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={8}
                dataKey="value"
                labelLine={false}
                label={false}
                startAngle={90}
                endAngle={-270}
              >
                {chartData.map((entry) => {
                  const color = LENDER_COLORS[entry.name] || '#8B5CF6';
                  return <Cell key={entry.name} fill={color} stroke="#fff" strokeWidth={2} />;
                })}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={renderLegend} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="w-full h-56 flex items-center justify-center">
          <div className="text-gray-500">No merchant by lender data available.</div>
        </div>
      )}

      {!isLoading && stats && (
        <div className="mt-5 border-t border-gray-200 pt-4">
          <div className="flex justify-between">
            <div className="text-center">
              <p className="text-sm text-gray-500">Total Merchants</p>
              <p className="text-xl font-gilroy-bold text-brand-blue">{stats.totalMerchants.toLocaleString()}</p>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500">Most Popular</p>
              <p className="text-sm font-gilroy-semibold text-brand-blue">{getMostPopularLender()}</p>
            </div>
          </div>
        </div>
      )}
    </ChartCard>
  );
};

export default MerchantsByLender;
